import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

function DashboardGastos({ getAuthHeaders }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      const response = await axios.get(`${API}/dashboard-gastos/estatisticas`, getAuthHeaders());
      setStats(response.data);
    } catch (error) {
      toast.error('Erro ao carregar dados de gastos');
    }
  };

  if (!stats) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto" data-testid="dashboard-gastos">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Dashboard – Gastos</h1>

      {/* Cards Mês Corrente */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-700 mb-4 flex items-center">
          <span className="mr-2">📅</span> 
          {stats.mes_corrente.nome_mes} {stats.mes_corrente.ano}
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Box 1 - Gastos Mensais */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200" data-testid="box-gastos-mensais">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-red-100">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Gastos Mensais</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.mes_corrente.despesas_mensais)}</p>
                <p className="text-xs text-gray-500 mt-1">Despesas Mensais</p>
              </div>
            </div>
          </div>

          {/* Box 2 - Parcelas do Mês */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200" data-testid="box-parcelas-mes">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-orange-100">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Parcelas do Mês</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.mes_corrente.parcelas)}</p>
                <p className="text-xs text-gray-500 mt-1">Compras Parceladas</p>
              </div>
            </div>
          </div>

          {/* Box 3 - Gasto Total do Mês */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 border-l-4 border-l-red-500" data-testid="box-total-mes">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-red-100">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Gasto Total do Mês</p>
                <p className="text-2xl font-bold text-red-600">{formatCurrency(stats.mes_corrente.total)}</p>
                <p className="text-xs text-gray-500 mt-1">Gastos + Parcelas</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Histórico - Últimos 3 Meses */}
      <div>
        <h2 className="text-xl font-semibold text-gray-700 mb-4 flex items-center">
          <span className="mr-2">📊</span> 
          Histórico de Gastos (Últimos 3 Meses)
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {stats.historico.map((mes, index) => (
            <div 
              key={`${mes.ano}-${mes.mes}`}
              className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
              data-testid={`box-historico-${index + 1}`}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">
                  {mes.nome_mes} {mes.ano}
                </h3>
                <span className="text-sm text-gray-500">Mês -{index + 1}</span>
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">Gastos Mensais</span>
                  <span className="font-medium">{formatCurrency(mes.despesas_mensais)}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">Parcelas</span>
                  <span className="font-medium">{formatCurrency(mes.parcelas)}</span>
                </div>
                <div className="border-t pt-2 flex justify-between items-center">
                  <span className="font-semibold text-gray-700">Total</span>
                  <span className="font-bold text-red-600 text-lg">{formatCurrency(mes.total)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Info Box */}
      <div className="mt-8 bg-gray-50 p-4 rounded-lg border border-gray-200">
        <p className="text-sm text-gray-600 flex items-center">
          <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Este dashboard exibe apenas leitura e agregação dos dados cadastrados em "Finanças Pessoais".
        </p>
      </div>
    </div>
  );
}

export default DashboardGastos;
